import { Controller } from "@hotwired/stimulus"

// A disclosure, not an ARIA menu: the items are plain links reached with Tab,
// so the button only reports open/closed through aria-expanded.
export default class extends Controller {
  static targets = ["menu", "button"]

  connect() {
    this.boundCloseFromEvent = () => this.close()
    document.addEventListener("dropdown:close", this.boundCloseFromEvent)
    // Turbo snapshots the live DOM when the next visit starts: an open menu
    // would restore stale, reconnected without its document listeners. Not
    // left to pulse_controller's before-cache dropdown:close — that host is
    // not on every page a dropdown can be on
    document.addEventListener("turbo:before-cache", this.boundCloseFromEvent)
  }

  toggle(event) {
    event.stopPropagation()

    if (!this.menuTarget.hidden) {
      this.close()
      return
    }

    // Every instance closes itself on this event, taking its own document
    // listeners with it; poking the other instances' DOM from here left
    // their listeners attached (#3114). Ours is closed, so the synchronous
    // dispatch is a no-op for us.
    document.dispatchEvent(new CustomEvent("dropdown:close"))

    this.menuTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.buttonTarget.classList.add("tag__menu--active")
    this.positionDropdown()
    this.boundClickOutside = this.closeOnClickOutside.bind(this)
    this.boundEscape = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundEscape)
  }

  positionDropdown() {
    this.menuTarget.classList.remove("tag__dropdown--flip-x", "tag__dropdown--flip-y")
    requestAnimationFrame(() => {
      const menu = this.menuTarget.getBoundingClientRect()
      const button = this.buttonTarget.getBoundingClientRect()
      const gap = menu.top - button.bottom

      if (menu.left < 0 || menu.right > window.innerWidth) {
        this.menuTarget.classList.add("tag__dropdown--flip-x")
      }
      // Not just `bottom > innerHeight`: off the top edge can't be scrolled to (#2925).
      if (menu.bottom > window.innerHeight && menu.height + gap <= button.top) {
        this.menuTarget.classList.add("tag__dropdown--flip-y")
      }
    })
  }

  close() {
    if (this.menuTarget.hidden) return

    this.menuTarget.hidden = true
    this.buttonTarget.classList.remove("tag__menu--active")
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.removeOpenListeners()
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  removeOpenListeners() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundEscape)
  }

  disconnect() {
    this.removeOpenListeners()
    document.removeEventListener("dropdown:close", this.boundCloseFromEvent)
    document.removeEventListener("turbo:before-cache", this.boundCloseFromEvent)
  }
}
